import { Dispatch, MutableRefObject, SetStateAction } from 'react';

import { Button, Checkbox, Divider, Input, Radio, RadioGroup, Text, ValidationMessage } from '@thefarmersfront/kitchen';
import { isDeepEqual } from '@thefarmersfront/kitchen-utensil';

import { FormField } from '~/components/common';

import { useKakaoPostcodeSearch } from '~/hooks/common';
import { validateNumberAndHyphenOnly } from '~/utils';

import { DEFAULT_INPUT_STATUS } from '~/constants';

import { type TypeVenderSupplierDetailErrorFieldRefs } from '../VenderSupplierDetailModal/VenderSupplierDetailModal';

import type { TypeSupplierDetailFormStatus, TypeSupplierDetailFormValue } from '~/types/vender.types';

import styles from './VenderSupplierDetailModalShippingAddressInfoSection.module.scss';

type TypeVenderSupplierDetailModalShippingAddressInfoSectionProps = {
  errorFieldRefs: MutableRefObject<
    Pick<
      TypeVenderSupplierDetailErrorFieldRefs,
      | 'pickupType'
      | 'kurlyPickupZipCode'
      | 'kurlyPickupAddress'
      | 'kurlyPickupContactNumber'
      | 'milkRunPickupZipCode'
      | 'milkRunPickupAddress'
      | 'milkRunPickupContactNumber'
    >
  >;
  venderSupplierDetailForm: TypeSupplierDetailFormValue;
  venderSupplierDetailFormStatus: TypeSupplierDetailFormStatus;
  setVenderSupplierDetailForm: Dispatch<SetStateAction<TypeSupplierDetailFormValue>>;
  setVenderSupplierDetailFormStatus: Dispatch<SetStateAction<TypeSupplierDetailFormStatus>>;
};

/**
 * [화주사정보관리 > 공급사관리] 공급사 상세 모달 컴포넌트 > 출고지 정보 영역
 */
const VenderSupplierDetailModalShippingAddressInfoSection = ({
  errorFieldRefs,
  venderSupplierDetailForm,
  venderSupplierDetailFormStatus,
  setVenderSupplierDetailForm,
  setVenderSupplierDetailFormStatus,
}: TypeVenderSupplierDetailModalShippingAddressInfoSectionProps) => {
  const { handleZipCodeSearchOpen: handleKurlyPickupZipCodeSearchOpen } = useKakaoPostcodeSearch({
    onComplete: (zipCode, address) => {
      setVenderSupplierDetailForm((prev) => ({
        ...prev,
        kurlyPickupInfo: { zipCode, address, detailAddress: '', contactNumber: '' },
      }));
    },
  });

  const { handleZipCodeSearchOpen: handleMilkRunPickupZipCodeSearchOpen } = useKakaoPostcodeSearch({
    onComplete: (zipCode, address) => {
      setVenderSupplierDetailForm((prev) => ({
        ...prev,
        milkRunPickupInfo: { zipCode, address, detailAddress: '', contactNumber: '' },
      }));
    },
  });

  const handleChangeValue = <K extends keyof TypeSupplierDetailFormValue>(
    key: K,
    value: TypeSupplierDetailFormValue[K]
  ) => {
    setVenderSupplierDetailForm({ ...venderSupplierDetailForm, [key]: value });
  };

  const handleChangeStatusToDefault = (
    key: keyof TypeSupplierDetailFormStatus | (keyof TypeSupplierDetailFormStatus)[]
  ) => {
    if (Array.isArray(key)) {
      const newStatus = key.reduce(
        (acc, curr) => {
          acc[curr] = DEFAULT_INPUT_STATUS;
          return acc;
        },
        { ...venderSupplierDetailFormStatus }
      );

      setVenderSupplierDetailFormStatus(newStatus);
    } else {
      setVenderSupplierDetailFormStatus({ ...venderSupplierDetailFormStatus, [key]: DEFAULT_INPUT_STATUS });
    }
  };

  const handleCopyKurlyStatusToMilkRun = () => {
    setVenderSupplierDetailFormStatus((prev) => ({
      ...prev,
      milkRunPickupZipCode: prev.kurlyPickupZipCode,
      milkRunPickupAddress: prev.kurlyPickupAddress,
      milkRunPickupContactNumber: prev.kurlyPickupContactNumber,
    }));
  };

  return (
    <div className={styles['shipping-address-info-section']}>
      <Text className={styles['title']} size="xxlarge" weight="bold">
        출고지 정보
      </Text>
      <div className={styles['section-container']}>
        <FormField
          label="픽업 서비스"
          required
          tooltip={{ description: '입고 요청 시 픽업 서비스를 이용하려면\n출고지 정보를 등록해야 합니다.' }}
          contents={
            <RadioGroup
              className={styles['radio-group']}
              name="use-pickup-service"
              size="medium"
              value={venderSupplierDetailForm.isUsePickup ? 'Y' : 'N'}
              onChange={(e) => handleChangeValue('isUsePickup', e.target.value === 'Y')}
            >
              <Radio value="Y" label="사용" />
              <Radio value="N" label="사용안함" />
            </RadioGroup>
          }
        />
        {venderSupplierDetailForm.isUsePickup && (
          <FormField
            ref={(el) => (errorFieldRefs.current.pickupType = el)}
            label="픽업 유형"
            required
            contents={
              <div className={styles['checkbox-group']}>
                <div className={styles['checkbox-wrapper']}>
                  <Checkbox
                    size="medium"
                    label="컬리픽업"
                    checked={venderSupplierDetailForm.isUseKurlyPickup}
                    status={venderSupplierDetailFormStatus.pickupType.status}
                    onChange={(e) => {
                      handleChangeValue('isUseKurlyPickup', e.target.checked);
                      handleChangeStatusToDefault('pickupType');
                    }}
                  />
                  <Checkbox
                    size="medium"
                    label="밀크런픽업"
                    checked={venderSupplierDetailForm.isUseMilkRunPickup}
                    status={venderSupplierDetailFormStatus.pickupType.status}
                    onChange={(e) => {
                      handleChangeValue('isUseMilkRunPickup', e.target.checked);
                      handleChangeStatusToDefault('pickupType');
                    }}
                  />
                </div>
                {venderSupplierDetailFormStatus.pickupType.status === 'error' && (
                  <ValidationMessage
                    className={styles['checkbox-validation-message']}
                    message={venderSupplierDetailFormStatus.pickupType.message ?? ''}
                  />
                )}
              </div>
            }
          />
        )}
      </div>
      {venderSupplierDetailForm.isUsePickup && venderSupplierDetailForm.isUseKurlyPickup && (
        <>
          <Divider color="gray100" />
          <div className={styles['section-container']}>
            <Text className={styles['section-title']} size="large" weight="bold">
              컬리픽업 출고지
            </Text>
            <FormField
              label="우편번호"
              required
              contents={
                <>
                  <Input
                    ref={(el) => (errorFieldRefs.current.kurlyPickupZipCode = el)}
                    type="text"
                    size="small"
                    maxWidth="229px"
                    disabled
                    status={venderSupplierDetailFormStatus.kurlyPickupZipCode.status}
                    validationMessage={venderSupplierDetailFormStatus.kurlyPickupZipCode.message ?? ''}
                    value={venderSupplierDetailForm.kurlyPickupInfo.zipCode}
                  />
                  <Button
                    className={styles['address-search-button']}
                    color="secondary"
                    size="small"
                    onClick={() => {
                      handleKurlyPickupZipCodeSearchOpen();
                      handleChangeStatusToDefault(['kurlyPickupZipCode', 'kurlyPickupAddress']);
                    }}
                  >
                    검색
                  </Button>
                </>
              }
            />
            <FormField
              label="기본주소"
              required
              contents={
                <Input
                  ref={(el) => (errorFieldRefs.current.kurlyPickupAddress = el)}
                  type="text"
                  size="small"
                  maxWidth="309px"
                  disabled
                  status={venderSupplierDetailFormStatus.kurlyPickupAddress.status}
                  validationMessage={venderSupplierDetailFormStatus.kurlyPickupAddress.message ?? ''}
                  value={venderSupplierDetailForm.kurlyPickupInfo.address}
                />
              }
            />
            <FormField
              label="상세주소"
              contents={
                <Input
                  type="text"
                  size="small"
                  maxWidth="309px"
                  placeholder="입력"
                  value={venderSupplierDetailForm.kurlyPickupInfo.detailAddress}
                  onChange={(e) =>
                    handleChangeValue('kurlyPickupInfo', {
                      ...venderSupplierDetailForm.kurlyPickupInfo,
                      detailAddress: e.target.value,
                    } as TypeSupplierDetailFormValue['kurlyPickupInfo'])
                  }
                  onBlur={(e) =>
                    handleChangeValue('kurlyPickupInfo', {
                      ...venderSupplierDetailForm.kurlyPickupInfo,
                      detailAddress: e.target.value.trim(),
                    } as TypeSupplierDetailFormValue['kurlyPickupInfo'])
                  }
                />
              }
            />
            <FormField
              label="연락처"
              required
              contents={
                <Input
                  ref={(el) => (errorFieldRefs.current.kurlyPickupContactNumber = el)}
                  type="text"
                  size="small"
                  maxWidth="229px"
                  placeholder="- 포함 입력"
                  status={venderSupplierDetailFormStatus.kurlyPickupContactNumber.status}
                  validationMessage={venderSupplierDetailFormStatus.kurlyPickupContactNumber.message ?? ''}
                  value={venderSupplierDetailForm.kurlyPickupInfo.contactNumber}
                  onChange={(e) => {
                    if (!validateNumberAndHyphenOnly(e.target.value)) return;

                    handleChangeValue('kurlyPickupInfo', {
                      ...venderSupplierDetailForm.kurlyPickupInfo,
                      contactNumber: e.target.value,
                    } as TypeSupplierDetailFormValue['kurlyPickupInfo']);
                    handleChangeStatusToDefault('kurlyPickupContactNumber');
                  }}
                  onBlur={(e) =>
                    handleChangeValue('kurlyPickupInfo', {
                      ...venderSupplierDetailForm.kurlyPickupInfo,
                      contactNumber: e.target.value.trim(),
                    } as TypeSupplierDetailFormValue['kurlyPickupInfo'])
                  }
                  onClick={() => handleChangeStatusToDefault('kurlyPickupContactNumber')}
                />
              }
            />
          </div>
        </>
      )}
      {venderSupplierDetailForm.isUsePickup && venderSupplierDetailForm.isUseMilkRunPickup && (
        <>
          <Divider color="gray100" />
          <div className={styles['section-container']}>
            <Text className={styles['section-title']} size="large" weight="bold">
              밀크런픽업 출고지
            </Text>
            <FormField
              label="우편번호"
              required
              contents={
                <>
                  <Input
                    ref={(el) => (errorFieldRefs.current.milkRunPickupZipCode = el)}
                    type="text"
                    size="small"
                    maxWidth="229px"
                    disabled
                    status={venderSupplierDetailFormStatus.milkRunPickupZipCode.status}
                    validationMessage={venderSupplierDetailFormStatus.milkRunPickupZipCode.message ?? ''}
                    value={venderSupplierDetailForm.milkRunPickupInfo.zipCode}
                  />
                  <Button
                    className={styles['address-search-button']}
                    color="secondary"
                    size="small"
                    onClick={() => {
                      handleMilkRunPickupZipCodeSearchOpen();
                      handleChangeStatusToDefault(['milkRunPickupZipCode', 'milkRunPickupAddress']);
                    }}
                  >
                    검색
                  </Button>
                  {venderSupplierDetailForm.isUseKurlyPickup && (
                    <Checkbox
                      className={styles['same-address-checkbox']}
                      size="medium"
                      label="컬리픽업과 동일"
                      checked={
                        (Object.values(venderSupplierDetailForm.kurlyPickupInfo).some((value) => value.length > 0) ||
                          Object.values(venderSupplierDetailForm.milkRunPickupInfo).some(
                            (value) => value.length > 0
                          )) &&
                        isDeepEqual(
                          venderSupplierDetailForm.kurlyPickupInfo,
                          venderSupplierDetailForm.milkRunPickupInfo
                        )
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleChangeValue('milkRunPickupInfo', venderSupplierDetailForm.kurlyPickupInfo);
                          handleCopyKurlyStatusToMilkRun();
                        } else {
                          handleChangeValue('milkRunPickupInfo', {
                            zipCode: '',
                            address: '',
                            detailAddress: '',
                            contactNumber: '',
                          });
                          handleChangeStatusToDefault([
                            'milkRunPickupZipCode',
                            'milkRunPickupAddress',
                            'milkRunPickupContactNumber',
                          ]);
                        }
                      }}
                    />
                  )}
                </>
              }
            />
            <FormField
              label="기본주소"
              required
              contents={
                <Input
                  ref={(el) => (errorFieldRefs.current.milkRunPickupAddress = el)}
                  type="text"
                  size="small"
                  maxWidth="309px"
                  disabled
                  status={venderSupplierDetailFormStatus.milkRunPickupAddress.status}
                  validationMessage={venderSupplierDetailFormStatus.milkRunPickupAddress.message ?? ''}
                  value={venderSupplierDetailForm.milkRunPickupInfo.address}
                />
              }
            />
            <FormField
              label="상세주소"
              contents={
                <Input
                  type="text"
                  size="small"
                  maxWidth="309px"
                  placeholder="입력"
                  value={venderSupplierDetailForm.milkRunPickupInfo.detailAddress}
                  onChange={(e) =>
                    handleChangeValue('milkRunPickupInfo', {
                      ...venderSupplierDetailForm.milkRunPickupInfo,
                      detailAddress: e.target.value,
                    } as TypeSupplierDetailFormValue['milkRunPickupInfo'])
                  }
                  onBlur={(e) =>
                    handleChangeValue('milkRunPickupInfo', {
                      ...venderSupplierDetailForm.milkRunPickupInfo,
                      detailAddress: e.target.value.trim(),
                    } as TypeSupplierDetailFormValue['milkRunPickupInfo'])
                  }
                />
              }
            />
            <FormField
              label="연락처"
              required
              contents={
                <Input
                  ref={(el) => (errorFieldRefs.current.milkRunPickupContactNumber = el)}
                  type="text"
                  size="small"
                  maxWidth="229px"
                  placeholder="- 포함 입력"
                  status={venderSupplierDetailFormStatus.milkRunPickupContactNumber.status}
                  validationMessage={venderSupplierDetailFormStatus.milkRunPickupContactNumber.message ?? ''}
                  value={venderSupplierDetailForm.milkRunPickupInfo.contactNumber}
                  onChange={(e) => {
                    if (!validateNumberAndHyphenOnly(e.target.value)) return;

                    handleChangeValue('milkRunPickupInfo', {
                      ...venderSupplierDetailForm.milkRunPickupInfo,
                      contactNumber: e.target.value,
                    } as TypeSupplierDetailFormValue['milkRunPickupInfo']);
                    handleChangeStatusToDefault('milkRunPickupContactNumber');
                  }}
                  onBlur={(e) =>
                    handleChangeValue('milkRunPickupInfo', {
                      ...venderSupplierDetailForm.milkRunPickupInfo,
                      contactNumber: e.target.value.trim(),
                    } as TypeSupplierDetailFormValue['milkRunPickupInfo'])
                  }
                  onClick={() => handleChangeStatusToDefault('milkRunPickupContactNumber')}
                />
              }
            />
          </div>
        </>
      )}
    </div>
  );
};

export default VenderSupplierDetailModalShippingAddressInfoSection;
